// Accounts and sign-in (crypto-launch-prompt lane A, money-mode-gaps §1.1–§1.2, §2 "Username"):
// the username rule and the payment-wallet rule both sides apply, and the JSON shapes of the
// authenticated HTTP API, so the server (http/api.ts) and the client (net/account-api.ts) share
// one copy.
//
// Identity: a player signs in with Privy (email, Google, X or a Solana wallet). The account id is
// the Privy user id (a DID such as `did:privy:cm…`), taken by the server from a verified access
// token, never from the client. Every `/api/account/*` and `/api/money/*` route except
// `GET /api/money/find` needs `Authorization: Bearer <access token>`; the game socket carries the
// same token in HELLO (`authToken`). Free play needs no sign-in.
//
// Username: unique (case-insensitive), 3–15 characters of ASCII letters, digits and `_`, set once
// after the first sign-in, shown in money rooms and leaderboards (free rooms keep the nick field).
// The server stores it as typed and enforces uniqueness on `usernameKey`, a look-alike skeleton
// (lower case, `rn`→`m`, `vv`→`w`, `i`/`1`→`l`, `0`→`o`), so "BaIIio_Kim" collides with
// "Ballio_Kim". Names that read as staff or the game itself are reserved (`reserved`), compared
// on the same skeleton (security audit, lane A). migrations/0002 keeps the database CHECK on
// `username_key` identical to `usernameKey`.
//
// Contracts step: complete. Consumers: server http/api.ts + accounts repository, client auth
// module and menu.

/** Shortest and longest username (characters = bytes: the alphabet is ASCII). */
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 15;

const USERNAME_RE = /^[A-Za-z0-9_]+$/;

export type UsernameProblem = 'tooShort' | 'tooLong' | 'badChars' | 'reserved';

/** Reserved anywhere in a name (compared on the skeleton): staff and the game's own name. */
const RESERVED_PARTS: readonly string[] = [
  'admin',
  'moderator',
  'support',
  'official',
  'system',
  'staff',
  'privy',
];
/**
 * Reserved as the whole name only: short words that are also parts of ordinary names, and the
 * game's own name ("Ballio_Kim" is a player; "Ballio_Staff" is caught by `staff`).
 */
const RESERVED_NAMES: readonly string[] = [
  'ballio',
  'ball_io',
  'ballio_io',
  'mod',
  'mods',
  'root',
  'owner',
  'dev',
  'devs',
  'team',
  'server',
  'bot',
  'help',
  'null',
  'undefined',
  'anonymous',
];

/**
 * Why `name` is not a valid username, or null when it is. Leading/trailing spaces are NOT trimmed
 * here: callers trim user input first (`name.trim()`), the server stores exactly what it checked.
 */
export function usernameProblem(name: string): UsernameProblem | null {
  if (name.length < USERNAME_MIN) return 'tooShort';
  if (name.length > USERNAME_MAX) return 'tooLong';
  if (!USERNAME_RE.test(name)) return 'badChars';
  const key = usernameKey(name);
  if (RESERVED_NAMES.some((r) => key === usernameKey(r))) return 'reserved';
  if (RESERVED_PARTS.some((r) => key.includes(usernameKey(r)))) return 'reserved';
  return null;
}

export function isValidUsername(name: string): boolean {
  return usernameProblem(name) === null;
}

/**
 * The uniqueness key: a skeleton that folds ASCII look-alikes, so two names that read the same in
 * the UI font collide. Must stay identical to the SQL in migrations/0002_username_skeleton.sql:
 * `translate(replace(replace(lower(name), 'rn', 'm'), 'vv', 'w'), 'i10', 'llo')`.
 */
export function usernameKey(name: string): string {
  return name
    .toLowerCase()
    .replaceAll('rn', 'm')
    .replaceAll('vv', 'w')
    .replace(/[i1]/g, 'l')
    .replaceAll('0', 'o');
}

// --- the payment wallet (money-mode §11 "Payment wallet") ---------------------------------------
//
// A Privy user may hold several Solana wallets: the embedded one (Privy-made, created on login
// for users who have no wallet: email, Google and X sign-ins) and external ones (Phantom,
// Solflare, Backpack, … linked by signing in with them, or later). The account's PAYMENT wallet
// is the one buy-ins must come from (lane C checks sender = this address) and payouts go to:
//
//   1. an embedded Solana wallet, if any: the lowest HD `walletIndex` (Privy's primary is 0),
//      then the earliest `firstVerifiedAt`, then the smallest address;
//   2. else an external Solana wallet: the earliest `firstVerifiedAt` (the one the player signed
//      in with: it created the Privy user, every later link is newer), then the smallest address.
//
// "Embedded" means `walletClientType` privy / privy-v2 AND `connectorType` embedded
// (`walletKindOf`). Unknown values (null) sort last. The input order never matters, so the same
// linked accounts always give the same wallet. The server applies this only to a verified, fresh
// identity token (server/src/account/wallet.ts) and stores the result ONCE: a later embedded
// wallet or another linked wallet never moves an account's payment wallet. A stored wallet that
// is no longer linked is flagged (`walletUnlinkedAt`): no buy-ins, payouts held for an operator.

/** How a Solana wallet is held. */
export type WalletKind =
  /**
   * Privy-made (`walletClientType` `privy` / `privy-v2` AND `connectorType` `embedded`): Privy's
   * UI signs for the player.
   */
  | 'embedded'
  /** The player's own wallet app (Phantom, Solflare, Backpack, …), linked by a signature. */
  | 'external';

/** A linked Solana wallet, normalized from an identity token (server) or a Privy `User` (client). */
export interface LinkedSolanaWallet {
  /** Base58 address as Privy reports it. */
  readonly address: string;
  readonly kind: WalletKind;
  /** Embedded wallets: the HD index (0 = the first one made); null for external / unknown. */
  readonly walletIndex: number | null;
  /** When the wallet was first linked, epoch seconds; null when unknown. */
  readonly firstVerifiedAt: number | null;
}

/** Privy's `walletClientType` values of an embedded wallet. */
export const EMBEDDED_WALLET_CLIENT_TYPES: readonly string[] = ['privy', 'privy-v2'];
/** Privy's `connectorType` of an embedded wallet. */
export const EMBEDDED_WALLET_CONNECTOR_TYPE = 'embedded';

/**
 * The kind of a linked wallet. Embedded needs BOTH Privy labels, as Privy's own SDK checks
 * (js-sdk-core `getEmbeddedConnectedWallet`): an external wallet's `walletClientType` is what the
 * browser reported at SIWS login / link time, so `privy` alone proves nothing; `connectorType`
 * `embedded` is set only for wallets Privy made. Anything else (a missing label included) is
 * external.
 */
export function walletKindOf(
  walletClientType: string | null | undefined,
  connectorType: string | null | undefined,
): WalletKind {
  return typeof walletClientType === 'string' &&
    EMBEDDED_WALLET_CLIENT_TYPES.includes(walletClientType) &&
    connectorType === EMBEDDED_WALLET_CONNECTOR_TYPE
    ? 'embedded'
    : 'external';
}

/** A base58 Solana address (32–44 characters). */
export function isSolanaAddress(address: string): boolean {
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
}

const nullLast = (a: number | null, b: number | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a - b;

function walletOrder(a: LinkedSolanaWallet, b: LinkedSolanaWallet): number {
  return (
    nullLast(a.walletIndex, b.walletIndex) ||
    nullLast(a.firstVerifiedAt, b.firstVerifiedAt) ||
    (a.address < b.address ? -1 : a.address > b.address ? 1 : 0)
  );
}

/** The payment wallet among `wallets` (rule above), or null when there is none. */
export function selectPaymentWallet(
  wallets: readonly LinkedSolanaWallet[],
): LinkedSolanaWallet | null {
  for (const kind of ['embedded', 'external'] as const) {
    const of = wallets.filter((w) => w.kind === kind && w.address !== '');
    if (of.length > 0) return [...of].sort(walletOrder)[0] as LinkedSolanaWallet;
  }
  return null;
}

// --- HTTP API shapes (protocol §3.2) -------------------------------------------------------------

/** Error codes of the authenticated routes (HTTP status in brackets). */
export type ApiAuthError =
  /** [401] No `Authorization: Bearer` header, or not a bearer token. */
  | 'authRequired'
  /** [401] The token failed verification (expired, bad signature, another app). */
  | 'authInvalid'
  /** [503] Sign-in is not configured on this server. */
  | 'authUnavailable'
  /** [403] The account is banned (`bannedUntil` in the body). */
  | 'banned'
  /**
   * [429] Too many refused tokens from this address (or too many account writes): wait a
   * minute. The token was not checked.
   */
  | 'rateLimited';

/** Body of every 401 / 403 / 503 auth refusal. */
export interface ApiAuthErrorResponse {
  readonly status: 'error';
  readonly error: ApiAuthError;
  /** `banned` only: wall ms the ban ends (null = permanent). */
  readonly bannedUntil?: number | null;
}

/** `GET /api/account/me` 200: the signed-in account (created on first sight of a valid token). */
export interface ApiAccountMe {
  readonly status: 'ok';
  /** The Privy user id (DID). */
  readonly accountId: string;
  /** null until set (the client then asks for one before the first money game). */
  readonly username: string | null;
  /**
   * Base58 address of the account's payment wallet (embedded, else the external sign-in wallet:
   * `selectPaymentWallet`), null until the server learned it. Set once.
   */
  readonly wallet: string | null;
  /**
   * Wall ms the server saw `wallet` missing from the account's linked Privy wallets (unlinked in
   * Privy), null otherwise. While set the account buys nothing and its payouts are held for an
   * operator; it clears when a fresh identity token lists the wallet again (the player linked it
   * back) or an operator releases the wallet (money-mode §11 "Payment wallet"). Absent from an
   * older server: read as null.
   */
  readonly walletUnlinkedAt?: number | null;
  /** Wall ms the account row was created. */
  readonly createdAt: number;
  /** Wall ms a ban ends, or null when not banned. A permanent ban is PERMANENT_BAN_MS. */
  readonly bannedUntil: number | null;
}

/**
 * `ApiAccountMe.bannedUntil` of a permanent ban: 8.64e15, the last instant a JS Date can hold
 * (JSON has no Infinity). Anything at or past it is "permanent".
 */
export const PERMANENT_BAN_MS = 8.64e15;

/** `POST /api/account/username` body. */
export interface ApiSetUsernameRequest {
  readonly username: string;
}

/**
 * `POST /api/account/username` → 200 ok (the account, username now set) | 400 invalid (with
 * `problem`) | 409 taken (someone has it, any case) | 409 alreadySet (usernames are set once) |
 * 401/403/503 auth errors.
 */
export type ApiSetUsernameResponse =
  | ApiAccountMe
  | { readonly status: 'error'; readonly error: 'invalid'; readonly problem: UsernameProblem }
  | { readonly status: 'error'; readonly error: 'taken' | 'alreadySet' }
  | ApiAuthErrorResponse;

/**
 * `POST /api/account/wallet` body: the client's Privy **identity token** (same ES256 key as the
 * access token; carries the linked accounts), at most `MAX_IDENTITY_TOKEN_BYTES` long and minted
 * at most `IDENTITY_TOKEN_MAX_AGE_SEC` ago. The server picks the payment wallet from its linked
 * Solana wallets (`selectPaymentWallet`) and stores it on the account once. Later calls answer
 * 200 while the stored wallet is still linked (whatever else was linked since; this also clears
 * `walletUnlinkedAt`) and 409 walletMismatch once it is not (the server then sets
 * `walletUnlinkedAt`). The client calls it when the server has no wallet yet, and whenever the
 * stored wallet's presence among the Privy user's linked wallets disagrees with
 * `walletUnlinkedAt`.
 */
export interface ApiLinkWalletRequest {
  readonly identityToken: string;
}

/** Longest identity token the wallet route reads (it carries every linked account). */
export const MAX_IDENTITY_TOKEN_BYTES = 8192;
/**
 * Oldest identity token (`iat`) the wallet route accepts, seconds: a token minted before the
 * player unlinked a wallet must not prove that wallet is still linked. The client refreshes the
 * Privy user (a new identity token) and retries once on 401 identityStale.
 */
export const IDENTITY_TOKEN_MAX_AGE_SEC = 600;

/**
 * `POST /api/account/wallet` → 200 ApiAccountMe | 400 noWallet | 401 authInvalid |
 * 401 identityStale | 409 walletMismatch.
 */
export type ApiLinkWalletResponse =
  | ApiAccountMe
  | {
      readonly status: 'error';
      readonly error: 'noWallet' | 'walletMismatch' | 'identityStale';
    }
  | ApiAuthErrorResponse;

// --- dev fake sign-in (auth.provider "fake") ------------------------------------------------------
//
// With the fake provider (dev and tests only; the server refuses it in production and with any
// real settlement) the client mints its own tokens: `fake.` + base64url(JSON claims). The server's
// FakeAuthVerifier checks the shape and `exp` only. There is no secret: anyone can be anyone,
// which is the point of a local dev login.

export const FAKE_AUTH_TOKEN_PREFIX = 'fake.';

export interface FakeAuthClaims {
  /** `access` (HELLO, Bearer) or `identity` (POST /api/account/wallet). */
  readonly kind: 'access' | 'identity';
  /** The account id (e.g. `did:privy:dev-k3j2…`). */
  readonly sub: string;
  /** Expiry, epoch seconds. */
  readonly exp: number;
  /** Issued at, epoch seconds (identity tokens: the wallet route's freshness check). */
  readonly iat?: number;
  /** Identity tokens: the embedded Solana wallet to report. */
  readonly wallet?: string;
  readonly email?: string;
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** base64url without padding (RFC 4648 §5) of the UTF-8 bytes of `text`. */
function base64UrlEncode(text: string): string {
  const b = new TextEncoder().encode(text);
  let out = '';
  for (let i = 0; i < b.length; i += 3) {
    const n = ((b[i] as number) << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    out += B64URL[(n >> 18) & 63] as string;
    out += B64URL[(n >> 12) & 63] as string;
    if (i + 1 < b.length) out += B64URL[(n >> 6) & 63] as string;
    if (i + 2 < b.length) out += B64URL[n & 63] as string;
  }
  return out;
}

/** The UTF-8 text of base64url `text`, or null when it is not valid base64url / UTF-8. */
function base64UrlDecode(text: string): string | null {
  if (text.length % 4 === 1) return null;
  const bytes: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of text) {
    const v = B64URL.indexOf(ch);
    if (v < 0) return null;
    acc = ((acc << 6) | v) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((acc >> bits) & 0xff);
    }
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(bytes));
  } catch {
    return null;
  }
}

export function encodeFakeAuthToken(claims: FakeAuthClaims): string {
  return FAKE_AUTH_TOKEN_PREFIX + base64UrlEncode(JSON.stringify(claims));
}

/** The claims of a fake token, or null when it is not one (shape only; no expiry check). */
export function decodeFakeAuthToken(token: string): FakeAuthClaims | null {
  if (!token.startsWith(FAKE_AUTH_TOKEN_PREFIX)) return null;
  const json = base64UrlDecode(token.slice(FAKE_AUTH_TOKEN_PREFIX.length));
  if (json === null) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const kind = o['kind'];
  const sub = o['sub'];
  const exp = o['exp'];
  if ((kind !== 'access' && kind !== 'identity') || typeof sub !== 'string') return null;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
  const out: { -readonly [K in keyof FakeAuthClaims]: FakeAuthClaims[K] } = { kind, sub, exp };
  if (typeof o['iat'] === 'number' && Number.isFinite(o['iat'])) out.iat = o['iat'];
  if (typeof o['wallet'] === 'string') out.wallet = o['wallet'];
  if (typeof o['email'] === 'string') out.email = o['email'];
  return out;
}
